import { Link } from 'react-router-dom'
import { ArrowRightIcon, CheckIcon } from '@heroicons/react/24/outline'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'

import { Button } from '../components/Button'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { api } from '../services/api'

export function HomePage() {
  // Fetch featured packages from API
  const {
    data: featuredPackages = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['packages', 'featured'],
    queryFn: () => api.packages.getFeatured(3),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const benefits = [
    {
      title: 'Complete Privacy',
      description: 'Your data stays on your infrastructure. No cloud dependencies.',
      icon: '🔒',
    },
    {
      title: 'Expert Setup',
      description: 'Professional deployment and configuration by self-hosting experts.',
      icon: '⚡',
    },
    {
      title: 'Ongoing Support',
      description: '24/7 monitoring, updates, and technical support included.',
      icon: '🛟',
    },
    {
      title: 'Hardware Ready',
      description: 'Pre-configured Sin City PC hardware optimized for self-hosting.',
      icon: '💻',
    },
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 text-white">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Self-Hosting
              <span className="text-primary-300"> Made Simple</span>
            </h1>
            <p className="text-xl md:text-2xl text-primary-100 mb-8 max-w-3xl mx-auto">
              Professional self-hosting services with expert setup, ongoing support,
              and premium hardware. Take control of your digital life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                as={Link}
                to="/packages"
                size="lg"
              >
                View Service Packages
                <ArrowRightIcon className="w-5 h-5 ml-2" />
              </Button>
              <Button
                as={Link}
                to="/about"
                variant="secondary"
                size="lg"
              >
                Learn More
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose nickfixit Self-Hosting?
            </motion.h2>
            <motion.p variants={itemVariants} className="text-xl text-gray-600 max-w-2xl mx-auto">
              We handle the complexity so you can enjoy the benefits of true digital independence.
            </motion.p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="text-center p-6 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="text-4xl mb-4">{benefit.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Featured Packages */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Featured Service Packages
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Choose from our carefully crafted packages or contact us for a custom solution.
            </p>
          </motion.div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 mb-4">Failed to load packages</p>
              <Button variant="outline" onClick={() => window.location.reload()}>
                Retry
              </Button>
            </div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12"
            >
              {featuredPackages.map((pkg, index) => (
              <motion.div key={pkg.id} variants={itemVariants}>
                <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                  <div className="w-full h-48 bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                    <span className="text-6xl">
                      {pkg.package_type === 'media' ? '🎬' :
                       pkg.package_type === 'ai_corporate' ? '🤖' : '💻'}
                    </span>
                  </div>
                  {pkg.badge_text && (
                    <div className="absolute top-4 right-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-600 text-white">
                        {pkg.badge_text}
                      </span>
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                    <p className="text-gray-600 mb-4">{pkg.short_description}</p>
                    <div className="mb-4">
                      <div className="flex items-baseline">
                        <span className="text-3xl font-bold text-gray-900">
                          {pkg.monthly_price_display || pkg.one_time_price_display}
                        </span>
                        {pkg.monthly_price_display && (
                          <span className="text-gray-500 ml-1">/month</span>
                        )}
                      </div>
                      {pkg.setup_fee_display && (
                        <div className="text-sm text-gray-500 mt-1">
                          + {pkg.setup_fee_display} setup fee
                        </div>
                      )}
                    </div>
                    <Button
                      as={Link}
                      to={`/packages/${pkg.slug}`}
                      className="w-full"
                      variant={pkg.is_featured ? 'primary' : 'outline'}
                    >
                      Learn More
                      <ArrowRightIcon className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Button
              as={Link}
              to="/packages"
              size="lg"
              variant="outline"
            >
              View All Packages
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Featured Toolkits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              MCP Toolkits
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Powerful Model Context Protocol toolkits for AI assistants. Ready-to-use tools for media management, productivity, and more.
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12"
          >
            {[
              {
                name: 'PlexAbyss',
                description: 'Comprehensive media server management with Plex, Radarr, Sonarr integration',
                icon: '🎬',
                features: ['22 available tools', 'Media automation', 'Quality profiles'],
                status: 'healthy',
                pricing: 'Free'
              },
              {
                name: 'YT-DLP',
                description: 'Video downloading and processing with format conversion',
                icon: '📹',
                features: ['4 available tools', 'Video downloads', 'Format conversion'],
                status: 'healthy',
                pricing: 'Free'
              },
              {
                name: 'Obsidian Abyss',
                description: 'Advanced markdown documentation management for Abyss platform',
                icon: '📝',
                features: ['8 available tools', 'Markdown editing', 'Wikilink support'],
                status: 'healthy',
                pricing: 'Free'
              }
            ].map((toolkit, index) => (
              <motion.div key={index} variants={itemVariants}>
                <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-200 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        <span className="text-3xl mr-3">{toolkit.icon}</span>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{toolkit.name}</h3>
                          <span className="inline-block text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">
                            {toolkit.status}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-4">{toolkit.description}</p>
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-1">
                        {toolkit.features.map((feature, idx) => (
                          <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-green-600 font-medium">{toolkit.pricing}</span>
                      <span className="text-sm text-gray-500">REST API</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Button
              as={Link}
              to="/toolkits"
              size="lg"
              variant="outline"
            >
              View All Toolkits
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-20 bg-primary-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                From Setup to Support - We've Got You Covered
              </h2>
              <div className="space-y-4">
                {[
                  'Professional installation and configuration',
                  'Custom domain setup with SSL certificates',
                  'Ongoing monitoring and maintenance',
                  'Regular security updates and backups',
                  'Expert technical support when you need it',
                  'Hardware recommendations and pre-configuration',
                ].map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <CheckIcon className="w-6 h-6 text-primary-300 mr-3 flex-shrink-0" />
                    <span className="text-primary-100">{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-8"
            >
              <h3 className="text-2xl font-bold mb-4">Ready to Get Started?</h3>
              <p className="text-primary-100 mb-6">
                Contact us for a free consultation and custom quote for your self-hosting needs.
              </p>
              <div className="space-y-3">
                <Button
                  as={Link}
                  to="/contact"
                  className="w-full !bg-white !text-primary-900 hover:!bg-primary-50 hover:!text-primary-900 border-0"
                >
                  Get Free Consultation
                </Button>
                <Button
                  as={Link}
                  to="/packages"
                  variant="outline"
                  className="w-full !border-white !bg-transparent !text-white hover:!bg-white/10 hover:!text-white"
                >
                  Browse Packages
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}